import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../config/firebase';

// Rate limit: max. 5 errors per minute per session to prevent infinite loops
const RATE_LIMIT_WINDOW_MS = 60 * 1000;
const RATE_LIMIT_MAX = 5;
const errorTimestamps: number[] = [];

export interface ClientErrorContext {
  component?: string;
  source?: 'boundary' | 'window.error' | 'unhandledrejection' | 'manual';
  extra?: Record<string, unknown>;
}

const THIRD_PARTY_NOISE_PATTERNS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'crisp.chat',
];

const isThirdPartyNoise = (stack: string, message: string, extra?: Record<string, unknown>): boolean => {
  const haystacks: string[] = [stack, message];
  if (extra && typeof extra.filename === 'string') haystacks.push(extra.filename);
  for (const pattern of THIRD_PARTY_NOISE_PATTERNS) {
    for (const h of haystacks) {
      if (h && h.includes(pattern)) return true;
    }
  }
  return false;
};

const isPWAStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
};

const shouldRateLimit = (): boolean => {
  const now = Date.now();
  // Drop timestamps outside the window
  while (errorTimestamps.length > 0 && now - errorTimestamps[0] > RATE_LIMIT_WINDOW_MS) {
    errorTimestamps.shift();
  }
  if (errorTimestamps.length >= RATE_LIMIT_MAX) {
    return true;
  }
  errorTimestamps.push(now);
  return false;
};

/**
 * Logs a client-side error to Firestore.
 * Fails silently — never throws, to avoid recursion.
 */
export const logClientError = async (
  error: unknown,
  context: ClientErrorContext = {}
): Promise<void> => {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    const message = err.message || 'Unknown error';
    const stack = err.stack || '';

    if (isThirdPartyNoise(stack, message, context.extra)) {
      // eslint-disable-next-line no-console
      console.debug('[errorLog] filtered third-party noise');
      return;
    }

    if (shouldRateLimit()) {
      // eslint-disable-next-line no-console
      console.warn('[errorLog] rate-limited');
      return;
    }

    const userId = auth.currentUser?.uid || null;
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';

    const payload: Record<string, unknown> = {
      userId,
      message: message.substring(0, 2000),
      stack: stack.substring(0, 5000),
      url,
      userAgent,
      isPWA: isPWAStandalone(),
      source: context.source || 'manual',
      timestamp: Timestamp.now(),
    };

    if (context.component) payload.component = context.component;
    if (context.extra) payload.extra = context.extra;

    await addDoc(collection(db, 'errorLogs'), payload);
  } catch (logError) {
    // Never throw from the logger — just print to console
    // eslint-disable-next-line no-console
    console.error('[errorLog] failed to write error log', logError);
  }
};

/**
 * Registers global error handlers (window.onerror + unhandledrejection).
 * Should be called once during app startup.
 */
export const registerGlobalErrorHandlers = (): void => {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    void logClientError(event.error || new Error(event.message || 'window.error'), {
      source: 'window.error',
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    void logClientError(
      reason instanceof Error ? reason : new Error(String(reason ?? 'unhandledrejection')),
      { source: 'unhandledrejection' }
    );
  });
};
